/**
 * Klientská vrstva doručení a statistiky.
 *
 *   • `resolveLayers` — jedno volání na načtení SPA (a při změně identity /
 *     souhlasu / po ttl). Fail-silent: jakákoli chyba ⇒ prázdný seznam,
 *     marketingová vrstva nikdy neshodí výlohu.
 *   • `statRecorder` — dávkuje majáky (impression/click/close/submit) a
 *     odesílá je `keepalive` fetchem při odchodu ze stránky nebo po 5 s.
 *     Agregát na serveru je nefalšovatelná pravda pro admin.
 */
import { api } from '../api';
import { readConsent } from '../tracking/consent';
import { getSessionId, getVisitorId } from '../tracking/ids';
import type { LayerDelivery, LayerDeliveryResponse, LayerStatKind } from './types';

export interface ResolveContext {
  readonly path: string;
  readonly lang: string;
  readonly device: 'desktop' | 'mobile';
}

const EMPTY: LayerDeliveryResponse = { layers: [], ttlSeconds: 300 };

/**
 * Zavolá doručovací endpoint. `visitor_id` odejde JEN se souhlasem s
 * analytikou (server ho jinak stejně nezpracuje). Chyba/timeout ⇒ prázdno.
 */
export async function resolveLayers(context: ResolveContext): Promise<LayerDeliveryResponse> {
  const consent = readConsent();
  try {
    const response = await api.resolveLayers({
      visitor_id: consent.analytics ? getVisitorId() : null,
      session_id: consent.analytics ? getSessionId() : null,
      consent: { analytics: consent.analytics, marketing: consent.marketing, version: 1 },
      context,
    });
    // Defenzivně: server je náš, ale ať nevykreslený tvar nespadne renderer.
    if (response == null || !Array.isArray(response.layers)) return EMPTY;
    return response;
  } catch {
    return EMPTY;
  }
}

/**
 * Načtení jedné vrstvy v náhledovém režimu (preview token z adminu). Cílení
 * i triggery server vypne — „Vyzkoušet ve výloze" ukáže reálný render.
 */
export async function resolvePreview(
  context: ResolveContext,
  preview: { layerId: string; token: string },
): Promise<readonly LayerDelivery[]> {
  try {
    const response = await api.resolveLayers({
      visitor_id: null,
      session_id: null,
      consent: { analytics: false, marketing: false, version: 1 },
      context,
      preview,
    });
    return Array.isArray(response?.layers) ? response.layers : [];
  } catch {
    return [];
  }
}

/* ----------------------------------------------------------- statistika */

interface StatEvent {
  readonly layer_id: string;
  readonly kind: LayerStatKind;
}

/** Kontrakt majáku: dávka do 10 položek. */
const STAT_BATCH_LIMIT = 10;
/** Flush nejpozději za 5 s od prvního majáku ve frontě. */
const STAT_FLUSH_MS = 5000;

class StatRecorder {
  private queue: StatEvent[] = [];
  private timer: number | undefined;
  private registered = false;

  record(layerId: string, kind: LayerStatKind): void {
    this.ensureListeners();
    this.queue.push({ layer_id: layerId, kind });
    if (this.queue.length >= STAT_BATCH_LIMIT) {
      this.flush(false);
      return;
    }
    if (this.timer === undefined) {
      this.timer = window.setTimeout(() => this.flush(false), STAT_FLUSH_MS);
    }
  }

  flush(keepalive: boolean): void {
    if (this.timer !== undefined) {
      window.clearTimeout(this.timer);
      this.timer = undefined;
    }
    if (this.queue.length === 0) return;
    const batch = this.queue.slice(0, STAT_BATCH_LIMIT);
    this.queue = this.queue.slice(STAT_BATCH_LIMIT);
    api.recordLayerStats(batch, keepalive);
    // Zbytek fronty (nad 10) dořeší další timer.
    if (this.queue.length > 0 && this.timer === undefined) {
      this.timer = window.setTimeout(() => this.flush(false), STAT_FLUSH_MS);
    }
  }

  private ensureListeners(): void {
    if (this.registered) return;
    this.registered = true;
    window.addEventListener('pagehide', () => this.flush(true));
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') this.flush(true);
    });
  }
}

export const statRecorder = new StatRecorder();
